/**
 * voice-v8 — Diktat an der Cursor-Stelle. (21.10.2026)
 *
 * Martin: „Wenn ich mit dem Cursor schon im Eingabefeld bin, möchte ich natürlich von dort auch etwas reinkopieren und
 * dann entsprechend mit dem Mikrofonzeichen automatisch in das Textfeld einbinden können, weil der Cursor ja schon
 * positioniert ist.“
 *
 * Das Mikro IM Feld (#composer-ptt) diktiert darum ins Feld — an die Stelle des Cursors, eine Markierung wird ersetzt.
 * Es sendet nie. Hier steht nur das Rechnen (ohne DOM), app.ts liest und schreibt das Feld:
 *
 *   - `stelleImFeld`  welche Stelle gilt, wenn der Text kommt (die Erkennung dauert; inzwischen kann sich viel tun)
 *   - `fuegeEin`      der neue Wert und wo der Cursor danach steht — mit passenden Leerzeichen
 *
 * Die Regeln, die nie brechen:
 *   - Nichts Bestehendes geht verloren. Ersetzt wird nur eine Markierung, die Martin JETZT im Feld hält, oder die er
 *     beim Druck hielt, solange das Feld seitdem unverändert ist.
 *   - Hat sich das Feld geändert und Martin ist nicht mehr darin, kommt der Text ans Ende — dorthin, wo nichts
 *     verschoben sein kann.
 */

/** Wert und Markierung des Feldes in einem Augenblick. */
export interface FeldStand {
  wert: string;
  start: number;
  ende: number;
}

/** Die Stelle, an der eingefügt wird (`start === ende`: nur der Cursor, sonst wird die Markierung ersetzt). */
interface Stelle {
  start: number;
  ende: number;
}

function begrenze(n: number, max: number): number {
  return Number.isFinite(n) ? Math.min(Math.max(0, Math.trunc(n)), max) : max;
}

/**
 * Welche Stelle gilt, wenn der erkannte Text da ist?
 *   fokus      Martin steht im Feld — seine Markierung JETZT (auch wenn er inzwischen etwas eingefügt hat)
 *   unverändert  das Feld ist, wie es beim Druck war — die Markierung von damals
 *   sonst      das Ende (nie mitten in Text, der sich verschoben hat)
 * Ersetzt wird nur die Markierung vom Druck; eine andere Markierung wird zum Cursor an ihrem Ende.
 */
export function stelleImFeld(beimDruck: FeldStand, jetzt: FeldStand & { fokus: boolean }): Stelle {
  const n = jetzt.wert.length;
  const unveraendert = jetzt.wert === beimDruck.wert;
  const quelle = jetzt.fokus ? jetzt : unveraendert ? beimDruck : { start: n, ende: n };
  const start = begrenze(Math.min(quelle.start, quelle.ende), n);
  const ende = begrenze(Math.max(quelle.start, quelle.ende), n);
  /*
   * Prüfauflage 4 (21.10.2026): ersetzt wird nur die Markierung, die Martin beim DRUCK hielt (gleicher Text, gleiche
   * Grenzen). Eine inzwischen neu gesetzte Markierung kann ein Versehen sein (Doppeltipp, Markieren zum Kopieren) —
   * dann kommt der Text an ihr Ende, nichts wird überschrieben.
   */
  const druckStart = Math.min(beimDruck.start, beimDruck.ende);
  const druckEnde = Math.max(beimDruck.start, beimDruck.ende);
  const wieBeimDruck = unveraendert && start === druckStart && ende === druckEnde;
  return start === ende || wieBeimDruck ? { start, ende } : { start: ende, ende };
}

/** Kein Leerzeichen NACH diesen Zeichen (Zeilenanfang, Klammer, öffnendes Anführungszeichen). */
const OHNE_RAUM_DANACH = /[\s([{„“‚‘"'«‹/]$/u;
/** Kein Leerzeichen VOR diesen Zeichen (Satzzeichen, schließende Klammer, schließendes Anführungszeichen). */
const OHNE_RAUM_DAVOR = /^[\s.,;:!?…)\]}“”‘’"'»›/]/u;

/**
 * Fügt `text` an der Stelle ein. Ein Leerzeichen davor nur, wenn links etwas steht, das eins braucht (nicht am
 * Feld- oder Zeilenanfang, nie doppelt); eines danach nur, wenn rechts ein Wort direkt anschlösse. Der Cursor steht
 * danach direkt hinter dem eingefügten Text. Leerer Text ändert nichts.
 */
export function fuegeEin(wert: string, stelle: Stelle, text: string): { wert: string; cursor: number } {
  const start = begrenze(Math.min(stelle.start, stelle.ende), wert.length);
  const ende = begrenze(Math.max(stelle.start, stelle.ende), wert.length);
  const neu = text.trim();
  if (!neu) return { wert, cursor: ende };
  const davor = wert.slice(0, start);
  const danach = wert.slice(ende);
  const raumDavor = davor !== "" && !OHNE_RAUM_DANACH.test(davor) && !OHNE_RAUM_DAVOR.test(neu) ? " " : "";
  const raumDanach = danach !== "" && !OHNE_RAUM_DAVOR.test(danach) && !OHNE_RAUM_DANACH.test(neu) ? " " : "";
  const cursor = davor.length + raumDavor.length + neu.length;
  return { wert: davor + raumDavor + neu + raumDanach + danach, cursor };
}
